import {useCallback, useEffect, useRef} from 'react';

import {forceSignOut} from '../helpers/authHelpers';
import {useStores} from '../stores/use-stores';

/**
 * DIS-1826 D3: when `enabled`, signs the user out once per mount.
 *
 * The ref holds the in-flight promise, so a StrictMode double effect and
 * later `whenSignedOut` calls reuse the first `forceSignOut` run.
 * `whenSignedOut` resolves when that run finishes, or at once when disabled.
 */
export function useForcedSignOut(enabled: boolean): {whenSignedOut: () => Promise<void>} {
  const {userStore, rewardsStore} = useStores();
  const signOutRef = useRef<Promise<void> | null>(null);

  const start = useCallback((): Promise<void> => {
    if (!enabled) {
      return Promise.resolve();
    }
    if (!signOutRef.current) {
      signOutRef.current = forceSignOut(userStore, rewardsStore);
    }
    return signOutRef.current;
  }, [enabled, userStore, rewardsStore]);

  useEffect(() => {
    start();
  }, [start]);

  return {whenSignedOut: start};
}
